import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import {
  BillAccountsSelectors,
  BillingNotificationsState
} from '@amfam/billing/billaccount/data-access';
import { pendingPaymentActions } from '@amfam/billing/pending-payment/data-access';
// Store.
import { PendingRegistrationsActions } from '@amfam/billing/registration/data-access';
import { PolicySummarySelectors } from '@amfam/policy/data-access';
import { GoPaperLessSelectors } from '@amfam/policy/go-paperless/data-access';
import { PolicyAnalytics } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { BillAccount, BillingPaymentPaths, ONLINE_BILLING } from '@amfam/shared/models';
import { userQuery, UserState } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  ConfirmationModel,
  DockingBarService,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';

@Component({
  selector: '',
  templateUrl: './go-paperless-confirmation.component.html',
  styleUrls: ['./go-paperless-confirmation.component.scss'],
  standalone: false
})
export class GoPaperlessConfirmationComponent implements OnInit, OnDestroy {
  isEnrolled: boolean;
  user: UserState;
  isEZButtonEnrollment = false;
  private unenrolledBillingAccounts = new Array<BillAccount>();
  registeredBillAccounts = new Array<BillAccount>();
  homeSitePolicies = new Array();
  propertyServiceUrl: string;
  confirmationData: ConfirmationModel;
  updatedPreferenceBillAccounts: BillAccount[] = [];
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private analyticsFacade: AnalyticsFacade,
    private pageTitle: PageTitleService,
    private spinner: LoadingSpinnerService,
    private store: Store<fromRoot.RootState>,
    private dockingbarService: DockingBarService,
    private copyService: CopyService
  ) {
    this.spinner.start();
  }

  get promptToEnrollBillingAccounts(): boolean {
    return this.unenrolledBillingAccounts.length > 0;
  }

  enrollInPaperlessBilling() {
    for (const billingAccount of this.unenrolledBillingAccounts) {
      this.store.dispatch(
        PendingRegistrationsActions.PendingRegistrationsAddOne({
          associated: billingAccount.associated,
          policyNumber: billingAccount.policyList[0].policyNumber,
          billAccountNumber: billingAccount.billAccountNumber
        })
      );
    }
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.BILLING_REGISTRATION]
      })
    );
  }

  homeSiteLink() {
    window.open(this.propertyServiceUrl);
  }

  ngOnInit() {
    combineLatest(
      this.store.select(PolicySummarySelectors.selectHomesitePolicies),
      this.store.select(BrandSelectors.selectPropertyServiceUrl),
      this.store.select(routerQuery.getRouterPreviousState),
      (policies, url, prevRoute) => {
        return {
          policies: policies,
          url: url,
          prevRoute: prevRoute
        };
      }
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.propertyServiceUrl = state.url;
        if (
          state.url.indexOf('?') > 0 &&
          state.policies &&
          state.policies.length &&
          state.policies.length > 0
        ) {
          this.propertyServiceUrl =
            this.propertyServiceUrl + '&policyNumber=' + state.policies[0].policyNumber;
        }
        const prevRoute: string = _get(state, 'prevRoute.url', '');
        // Determines if GoPaperless was enrolled via EZ button
        this.isEZButtonEnrollment =
          prevRoute.indexOf('/overview') !== -1 || prevRoute.indexOf('/signature') !== -1;
        this.formatConfirmationData();
      });

    /* Clear if there are any pending payments(will be none though) to be on safe side
     which helps in navigate to confirmation instead payments(BillingPaymentPaths.ADD_SCHEDULED_PAYMENT).*/
    this.store.dispatch(pendingPaymentActions.ClearPendingPaymentsAction());

    this.pageTitle.set('Confirmation');
    this.dockingbarService.registerHeading('Confirmation');
    /*
     Get bill accounts that are

     1. not registered elsewhere (you cannot register an account that is registered to someone else)
     2. not enrollend in AFT (you cannot register AFT enrolled accounts)
     3. not already registered (you cannot register an account that is already registered)

     */

    combineLatest(
      this.store.select(BillAccountsSelectors.selectUnregisteredBillAccounts),
      this.store.select(BillAccountsSelectors.selectUserRegisteredBillAccounts),
      this.store.select(BillAccountsSelectors.selectBillAccountsNotifications),
      this.store.select(GoPaperLessSelectors.selectIsEnrolledGoPaperless),
      this.store.select(userQuery.selectUserState),
      this.store.select(PolicySummarySelectors.selectHomesitePolicies),
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      (
        unEnrolledbillAccounts,
        userRegisteredBillAccounts,
        billAccountsNotifications: BillingNotificationsState[],
        isEnrolled,
        user,
        homeSitePolicies,
        billaccounts: BillAccount[]
      ) => {
        return {
          unEnrolledbillAccounts: unEnrolledbillAccounts,
          userRegisteredBillAccounts: userRegisteredBillAccounts,
          billAccountsNotifications,
          isEnrolled: isEnrolled,
          user: user,
          homeSitePolicies: homeSitePolicies,
          billaccounts: billaccounts
        };
      }
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.unenrolledBillingAccounts = state.unEnrolledbillAccounts;
        this.registeredBillAccounts = state.userRegisteredBillAccounts.filter(billAccount => {
          return _get(billAccount, 'billingMethod') === ONLINE_BILLING;
        });
        const filteredBillAccountsNotifications = state.billAccountsNotifications.filter(
          billaccount => _get(billaccount, 'updatedDeliveryPreferences', false)
        );

        this.updatedPreferenceBillAccounts = state.billaccounts.filter(ba =>
          filteredBillAccountsNotifications.find(
            ban => ban.billAccountNumber === ba.billAccountNumber
          )
        );

        this.isEnrolled = state.isEnrolled;
        this.user = state.user;
        this.homeSitePolicies = state.homeSitePolicies;

        this.spinner.stop();
        this.formatConfirmationData();
      });

    // Keeping the analytics call out of the combined latest to avoid triggering it
    // multiple times.
    this.store
      .pipe(select(BillAccountsSelectors.selectUnregisteredBillAccounts), take(1))
      .subscribe(unEnrolledbillAccounts => {
        if (unEnrolledbillAccounts.length > 0) {
          this.analyticsFacade.trackPage(PolicyAnalytics.pageGoPaperlessConfirmation);
        }
      });
  }

  formatConfirmationData() {
    const notEasy = this.copyService.getCopy('goPaperless.ezButtonDisabledConfirmationTitle');
    const easy = this.copyService.getCopy('goPaperless.ezButtonEnabledConfirmationTitle');

    // create object
    this.confirmationData = {
      confirmation: {}
    };

    // header
    this.confirmationData.confirmation.heading = this.isEZButtonEnrollment ? easy : notEasy;

    // call to action
    if (this.homeSitePolicies.length > 0) {
      this.confirmationData.ctaArea = {
        heading: this.copyService.getCopy('goPaperless.goPaperlessHomesiteCtaTitle'),
        text: this.copyService.getCopy('goPaperless.goPaperlessHomesiteCtaText'),
        button: {
          title: 'Go To Homesite',
          callback: this.homeSiteLink.bind(this)
        }
      };
    }
  }

  backToOverview() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/overview']
      })
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
